import { type RefObject, useMemo } from 'react';

import { SkuType, type SkuDTO } from '@invenco/products-interface';

import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@/components/core/combobox';
import { useGateways } from '@/gateways/GatewayProvider';
import { useAutoCompleteQuery } from '@/shared/hooks/queries';

import { SkuBadge } from '../../components/sku-badge';

type Props = {
  excludedSkuIds: string[];
  portalContainerRef: RefObject<HTMLDivElement | null>;
  onSelect: (sku: SkuDTO) => void;
};

type SkuOption = {
  value: string;
  label: string;
  description: string | null;
  sku: SkuDTO;
};

export function SkuSearchCombobox({ excludedSkuIds, portalContainerRef, onSelect }: Props) {
  const { productsGateway } = useGateways();
  const { options: matchingSkus, onSearch } = useAutoCompleteQuery({
    parentKey: 'skus',
    queryParams: {
      type: SkuType.STANDARD,
      excludeSkuIds: excludedSkuIds,
    },
    query: (input, { signal }) => productsGateway.getSkus(input, { signal }),
    enabled: true,
  });

  const options = useMemo<SkuOption[]>(
    () =>
      matchingSkus.map((sku) => ({
        value: sku.id,
        label: sku.name,
        description: sku.description,
        sku,
      })),
    [matchingSkus],
  );

  return (
    <Combobox
      filteredItems={options} // To prevent items filtering locally
      items={options}
      onInputValueChange={(value) => onSearch(typeof value === 'string' ? value : '')}
      onValueChange={(value) => {
        const selectedValue = typeof value === 'string' ? value : undefined;
        if (!selectedValue) return;
        const selectedOption = options.find((option) => option.value === selectedValue);
        if (!selectedOption) return;
        onSelect(selectedOption.sku);
      }}
    >
      <ComboboxInput placeholder="Search by SKU or description" aria-label="SKU" showClear />
      <ComboboxContent portalContainer={portalContainerRef}>
        <ComboboxEmpty>No matching results</ComboboxEmpty>
        <ComboboxList>
          {(option: SkuOption) => (
            <ComboboxItem key={option.value} value={option.value}>
              <div className="flex min-w-0 items-center gap-2">
                <SkuBadge name={option.label} type={option.sku.type} />
                {option.description ? (
                  <span className="truncate text-sm">{option.description}</span>
                ) : null}
              </div>
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
}
